import * as React from 'react';
import { useTranslation } from "react-i18next";

import { AutoComplete } from '@progress/kendo-react-dropdowns';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { FloatingLabel } from '@progress/kendo-react-labels';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";

import { ProcessError, ErrorCode } from '../../classes/ExceptionProcessor';
import { ListOfPaged, MinimumPage } from '../../classes/consts';

import FarmsService from "../../services/farms.service";

export default function FarmFeedReceiptsComponents(props) {
    const { t } = useTranslation();
    // Error Hooks
    const [visibleError, setVisibleError] = React.useState(false);
    const [errorData, setErrorData] = React.useState({ messageError: "", code: "" });

    // Grid Hooks
    const [orderByString, setOrderByString] = React.useState("?order=description asc&filter=isClosed eq false");
    const [sortState, setSortState] = React.useState([]);
    const [pageState, setPageState] = React.useState({ skip: 0, take: MinimumPage() });

    // Loading Hooks
    const [loading, setLoading] = React.useState(true);
    const closeError = React.useCallback(() => { setVisibleError(false) }, []);

    const [data, setData] = React.useState([]);

    //#region DataFilters
    //#region Feed Receipts Description Filter
    const [feedReceiptDescriptionFilter, setFeedReceiptDescriptionFilter] = React.useState("");

    const handleFeedReceiptDescriptionFilterChange = (event) => {
        const value = event.value || "";
        setFeedReceiptDescriptionFilter(value);
    };
    //#endregion

    //#region Article Description Filter
    const [articleDescriptionFilter, setArticleDescriptionFilter] = React.useState("");

    const handleArticleDescriptionFilterChange = (event) => {
        const value = event.value || "";
        setArticleDescriptionFilter(value);
    };
    //#endregion

    //#region isClosed Filter
    const isClosedOptions = [
        { key: "open", label: t("FarmFeedReceipts_Status_Open") },
        { key: "closed", label: t("FarmFeedReceipts_Status_Closed") },
        { key: "all", label: t("FarmFeedReceipts_Status_All") }
    ];

    const [isClosedFilter, setIsClosedFilter] = React.useState("open");

    const handleIsClosedFilterChange = React.useCallback((e) => {
        let selected;
        if (e?.value !== undefined) selected = e.value;
        else if (e?.target && e.target.value !== undefined) selected = e.target.value;
        else if (e?.target && e.target.dataItem !== undefined) selected = e.target.dataItem;

        const key = selected?.key ?? selected ?? "all";
        setIsClosedFilter(key);
    }, []);
    //#endregion

    //#region Storage Point Receipt Type Filter
    const [storagePointReceiptTypeFilter, setStoragePointReceiptTypeFilter] = React.useState("");

    const handleStoragePointReceiptTypeFilterChange = (event) => {
        const value = event.value || "";
        setStoragePointReceiptTypeFilter(value);
    };
    //#endregion
    
    //#region Sorting Filter
    const applySortingAndFiltering = React.useCallback((sortOverride) => {
        const sort = sortOverride || sortState;

        const sortField = sort?.[0]?.field || "description";
        const sortDir = sort?.[0]?.dir || "asc";
        const order = `${sortField} ${sortDir}`;

        const filterParts = [];

        if (feedReceiptDescriptionFilter) {
            filterParts.push(`contains(description,'${feedReceiptDescriptionFilter}')`);
        }

        if (storagePointReceiptTypeFilter) {
            filterParts.push(`contains(storagePointReceiptTypeDescription,'${storagePointReceiptTypeFilter}')`);
        }

        if (articleDescriptionFilter) {
            filterParts.push(`contains(articleDescription,'${articleDescriptionFilter}')`);
        }

        if (isClosedFilter !== null) {
            filterParts.push(`isClosed eq ${isClosedFilter}`);
        }

        const filter = filterParts.join(" and ");
        const queryString = `?order=${encodeURIComponent(order)}${filter ? `&filter=${encodeURIComponent(filter)}` : ""}`;

        setOrderByString(queryString);
    }, [sortState, feedReceiptDescriptionFilter, storagePointReceiptTypeFilter, articleDescriptionFilter, isClosedFilter]);
    //#endregion
    //#endregion

    React.useEffect(() => 
    {
        if (props.farm.farmId !== 0)
        {
            setLoading(true);

            FarmsService.getFilteredFarmReceipts(props.farm.farmId, orderByString)
                .then(response => response.data)
                .then(json => {
                    setData(json.contract || []);
                    setLoading(false);
                }).catch(err => {
                    setErrorData({ messageError: ProcessError(err), code: ErrorCode(err) });
                    setVisibleError(true);
                    setLoading(false);
                });
        }
    }, [props.farm, orderByString]);

    const loadingPanel =
        <div className="k-loading-mask">
            <span className="k-loading-text">Loading</span>
            <div className="k-loading-image"></div>
            <div className="k-loading-color"></div>
        </div>;

    const loadingCell = React.useCallback((tdElement, cellProps) => {
        if (cellProps.dataItem[cellProps.field] === undefined) {
            // shows loading cell if no data
            return <td> <span className="k-placeholder-line"></span></td>;
        }

        // default rendering for this cell
        return tdElement;
    }, []);

    const sortChange = React.useCallback((event) => {
        setSortState(event.sort);
        applySortingAndFiltering(event.sort);
    });

    const pageChange = React.useCallback((event) => {
        setPageState({ ...pageState, skip: event.page.skip, take: event.page.take });
    }, [pageState]);

    const StatusCell = (props) => {
        const { dataItem } = props;
        const isActive = !dataItem.isClosed;

        return (
            <td style={{ textAlign: "center" }}>
                <span className={`k-icon ${isActive ? "k-i-check" : "k-i-close"}`} />
            </td>
        );
    };

    const processedData = (data.length > 0 || feedReceiptDescriptionFilter
        ? data
        : data).slice(pageState.skip, pageState.skip + pageState.take);

    return (
        <div>
            {visibleError && (
                <Dialog title={"Error " + errorData.code} onClose={closeError}>
                    <p style={{ margin: "25px", textAlign: "center" }}>
                        <b>{errorData.messageError}</b>
                    </p>
                    <DialogActionsBar>
                        <button className="k-button" onClick={() => { setVisibleError(false) }}>
                            OK
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {loading && loadingPanel}

            <div className="container-fluid">
                <div className="card">
                    <div className="card-header m-0 p-3">
                        <div className="row g-3 w-100 align-items-end">

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <FloatingLabel label={t("FarmFeedReceipts_Status")} editorId="isClosedInput">
                                    <DropDownList
                                        id="isClosedInput"
                                        loading={loading}
                                        style={{ width: '100%' }}
                                        data={isClosedOptions}
                                        textField="label"
                                        dataItemKey="key"
                                        value={isClosedOptions.find(opt => opt.key === isClosedFilter)}
                                        onChange={handleIsClosedFilterChange}
                                    />
                                </FloatingLabel>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <FloatingLabel
                                    label={t("FarmFeedReceipts_ReceiptType")}
                                    editorId="receiptTypeInput"
                                >
                                    <AutoComplete
                                        id="receiptTypeInput"
                                        loading={loading}
                                        style={{ width: '100%' }}
                                        data={[...new Set(data.map(r => r.storagePointReceiptTypeDescription))]}
                                        value={storagePointReceiptTypeFilter}
                                        onChange={handleStoragePointReceiptTypeFilterChange}
                                    />
                                </FloatingLabel>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <FloatingLabel label={t("FarmFeedReceipts_FeedReceiptDescription")} editorId="descriptionInput">
                                    <AutoComplete
                                        id="descriptionInput"
                                        loading={loading}
                                        style={{ width: '100%' }}
                                        data={[...new Set(data.map(feedReceipt => feedReceipt.description))]}
                                        value={feedReceiptDescriptionFilter}
                                        onChange={handleFeedReceiptDescriptionFilterChange}
                                    />
                                </FloatingLabel>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <FloatingLabel
                                    label={t("FarmFeedReceipts_ArticleDescription")}
                                    editorId="articleDescriptionInput"
                                >
                                    <AutoComplete
                                        id="articleDescriptionInput"
                                        loading={loading}
                                        style={{ width: '100%' }}
                                        data={[...new Set(data.map(r => r.articleDescription))]}
                                        value={articleDescriptionFilter}
                                        onChange={handleArticleDescriptionFilterChange}
                                    />
                                </FloatingLabel>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <button
                                    className="k-button k-primary w-100"
                                    style={{ marginTop: '25px' }}
                                    onClick={() => applySortingAndFiltering()}
                                    title={t("Farms_Button_ApplySorting")}
                                >
                                    {t("Farms_Button_ApplySorting")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <br />

                <div className="table-responsive">
                    <Grid
                        cellRender={loadingCell}
                        pageable={{ pageSizes: ListOfPaged() }}
                        data={processedData}
                        skip={pageState.skip}
                        take={pageState.take}
                        total={
                            (data.length > 0 ||
                                feedReceiptDescriptionFilter
                                ? data.length
                                : data.length)
                        }
                        scrollable={'none'}
                        sortable={{ allowUnsort: true, mode: "single" }}
                        sort={sortState}
                        onSortChange={sortChange}
                        onPageChange={pageChange}
                    >
                        <Column title={t("FarmFeedReceipts_Status")} cell={StatusCell} width="65px" sortable={false} />
                        <Column field="creationDate" title={t("FarmFeedReceipts_CreationDate")} sortable={true} />
                        <Column field="storagePointReceiptTypeDescription" title={t("FarmFeedReceipts_ReceiptType")} sortable={true} />
                        <Column field="articleId" title={t("FarmFeedReceipts_ArticleId")} sortable={true} />
                        <Column field="articleDescription" title={t("FarmFeedReceipts_ArticleDescription")} sortable={true} />
                        <Column field="quantity" title={t("FarmFeedReceipts_Quantity")} sortable={true} />
                    </Grid>
                </div>
            </div>
        </div>
    );
}